import { getCached, setCached, clearCache } from './cacheService';

const normalize = (value) => String(value || '').trim().toLowerCase();

const toMillis = (value) => {
  if (!value) return null;
  if (typeof value === 'number') return value;
  if (value?.toMillis) return value.toMillis();
  if (value?.seconds) return value.seconds * 1000;
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? null : parsed;
};

const EXPIRY_OPTIONS = {
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
  '1y': 365 * 24 * 60 * 60 * 1000,
  'never': null,
};

const isExpired = (expiresAt) => {
  if (!expiresAt) return false;
  const millis = toMillis(expiresAt);
  if (!millis) return false;
  return millis <= Date.now();
};

export const generateInviteToken = (length = 32) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  for (let i = 0; i < length; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
};

let jsonInvites = null;

const loadJsonInvites = () => {
  if (jsonInvites) return jsonInvites;

  try {
    const invitesData = require('../data/invites.json');
    const invites = invitesData.invites || [];

    jsonInvites = invites.map(invite => ({
      id: invite.id,
      token: invite.token,
      email: invite.email,
      phone: invite.phone,
      whatsapp: invite.whatsapp,
      albumId: invite.albumId,
      albumSlug: invite.albumSlug,
      role: invite.role,
      used: invite.used || false,
      createdAt: new Date(invite.createdAt).getTime(),
      expiresAt: invite.expiresAt ? new Date(invite.expiresAt).getTime() : null,
      usedAt: invite.usedAt ? new Date(invite.usedAt).getTime() : null,
      usedBy: invite.usedBy,
      createdBy: invite.createdBy,
      resendCount: invite.resendCount || 0,
      notes: invite.notes || '',
      source: 'json',
    }));

    console.log('[InviteService] Loaded', jsonInvites.length, 'invites from JSON');
    return jsonInvites;
  } catch (e) {
    console.error('[InviteService] Error loading JSON invites:', e);
    return [];
  }
};

export const getAllInvites = async (options = {}) => {
  const { status, albumId, limit: limitCount = 100, forceRefresh = false } = options;
  const cacheKey = `invites_${albumId || 'all'}`;

  if (!forceRefresh) {
    const cached = getCached(cacheKey);
    if (cached) {
      console.log('[InviteService] Returning cached invites:', cached.length);
      return cached;
    }
  }

  const invites = loadJsonInvites();

  let filtered = invites;
  if (albumId) {
    filtered = invites.filter(i => i.albumId === albumId);
  }

  if (status) {
    filtered = filtered.filter(invite => {
      if (status === 'active') return !invite.used && !isExpired(invite.expiresAt);
      if (status === 'used') return invite.used;
      if (status === 'expired') return !invite.used && isExpired(invite.expiresAt);
      return true;
    });
  }

  if (limitCount) {
    filtered = filtered.slice(0, limitCount);
  }

  setCached(cacheKey, filtered);
  console.log('[InviteService] Returning', filtered.length, 'invites from JSON');

  return filtered;
};

export const getInviteByToken = async (token) => {
  if (!token) return null;
  const invites = loadJsonInvites();
  return invites.find(i => i.token === token) || null;
};

export const getInviteById = async (inviteId) => {
  if (!inviteId) return null;
  const invites = loadJsonInvites();
  return invites.find(i => i.id === inviteId) || null;
};

export const createInvite = async (inviteData) => {
  console.log('[InviteService] Creating invite (JSON mode)...');

  const token = generateInviteToken();
  const now = Date.now();
  const expiresAt = EXPIRY_OPTIONS[inviteData.expiry]
    ? now + EXPIRY_OPTIONS[inviteData.expiry]
    : null;

  const newInvite = {
    id: `inv${String(now).slice(-6)}`,
    token,
    email: inviteData.email ? normalize(inviteData.email) : null,
    phone: inviteData.phone || '',
    whatsapp: inviteData.whatsapp || '',
    albumId: inviteData.albumId || 'sushflix',
    albumSlug: inviteData.albumSlug || 'sushflix',
    role: inviteData.role || 'invitee',
    createdAt: now,
    expiresAt,
    used: false,
    usedAt: null,
    usedBy: null,
    createdBy: inviteData.createdBy || 'admin',
    resendCount: 0,
    notes: inviteData.notes || '',
    source: 'json',
  };

  console.log('[InviteService] Invite created:', newInvite.id);

  return newInvite;
};

export const updateInvite = async (inviteId, updates) => {
  console.log('[InviteService] Update not supported in JSON mode - edit JSON file directly');
};

export const revokeInvite = async (inviteId) => {
  console.log('[InviteService] Revoke not supported in JSON mode - edit JSON file directly');
};

export const resendInvite = async (inviteId) => {
  console.log('[InviteService] Resend not supported in JSON mode - edit JSON file directly');
  return null;
};

export const markInviteAsUsed = async (inviteId, usedBy) => {
  console.log('[InviteService] Mark as used not supported in JSON mode - edit JSON file directly');
};

export const deleteInvite = async (inviteId) => {
  console.log('[InviteService] Delete not supported in JSON mode - edit JSON file directly');
};

export const getInviteStats = async (forceRefresh = false) => {
  const cacheKey = 'invite_stats';

  if (!forceRefresh) {
    const cached = getCached(cacheKey);
    if (cached) {
      console.log('[InviteService] Returning cached stats');
      return cached;
    }
  }

  const invites = loadJsonInvites();

  let active = 0;
  let used = 0;
  let expired = 0;

  invites.forEach(invite => {
    if (invite.used) {
      used++;
    } else if (isExpired(invite.expiresAt)) {
      expired++;
    } else {
      active++;
    }
  });

  const stats = {
    total: invites.length,
    active,
    used,
    expired,
  };

  setCached(cacheKey, stats);
  console.log('[InviteService] Returning stats:', stats);

return stats;
};

export const EXPIRY_OPTIONS_LIST = [
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days (default)' },
  { value: '1y', label: '1 year' },
  { value: 'never', label: 'Never expire' },
];

export const clearInviteCache = () => {
  clearCache('invites_all');
  clearCache('invites_sushflix');
  clearCache('invite_stats');
  console.log('[InviteService] Invite cache cleared');
};